import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, IconButton, Typography, InputBase } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SearchIcon from "@material-ui/icons/Search";
import { Colors } from "../theme/Theme";
import { AuthService } from "../api/AuthService";
import {
  getRegionalManagerLocationDisplayLabel,
  RegionalManagerDashboardResponse,
  RegionalManagerDashboardRow,
} from "../interfacesProps/RegionalManagerDashboardProps";

interface RegionalLocationDropdownProps {
  primaryColor: string;
  selectedLocationTitle: string;
  onSelectLocation: (row: RegionalManagerDashboardRow) => void;
  triggerLabel?: string;
}

export const RegionalLocationDropdown: React.FC<RegionalLocationDropdownProps> = ({
  primaryColor,
  selectedLocationTitle,
  onSelectLocation,
  triggerLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rows, setRows] = useState<RegionalManagerDashboardRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const fetchRows = async (pageNumber: number, shouldAppend = false) => {
    setIsLoading(true);
    try {
      const response: RegionalManagerDashboardResponse =
        await AuthService.getRegionalManagerDashboardView(
          6,
          pageNumber,
          debouncedSearchTerm
        );
      const fetchedRows = response?.data || [];

      setRows((prevRows) =>
        shouldAppend ? [...prevRows, ...fetchedRows] : fetchedRows
      );
      setPage(response?.pageInfo?.page || pageNumber);
      setTotalPages(response?.pageInfo?.pages || 1);

      if (!selectedLocationTitle && !shouldAppend && fetchedRows.length > 0) {
        onSelectLocation({
          ...fetchedRows[0],
          locationTitleName: getRegionalManagerLocationDisplayLabel(fetchedRows[0]),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 350);

    return () => {
      window.clearTimeout(debounceTimer);
    };
  }, [searchTerm]);

  useEffect(() => {
    setRows([]);
    setPage(1);
    setTotalPages(1);
    fetchRows(1);
  }, [selectedLocationTitle, debouncedSearchTerm]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const getInitials = (locationTitleName: string) => {
    const segments = locationTitleName
      .split("-")
      .join(" ")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    return segments
      .slice(0, 2)
      .map((segment) => segment.charAt(0).toUpperCase())
      .join("") || "RM";
  };

  const handleListScroll = async (
    event: React.UIEvent<HTMLDivElement, UIEvent>
  ) => {
    const target = event.currentTarget;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

    if (!reachedBottom || isLoading || page >= totalPages) {
      return;
    }

    await fetchRows(page + 1, true);
  };

  return (
    <div
      ref={dropdownRef}
      style={{ position: "relative", display: "flex", alignItems: "center" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          color: primaryColor,
          fontWeight: 600,
          cursor: "pointer",
          marginRight: 10,
        }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <Typography variant="body1" style={{ color: primaryColor, fontWeight: 600 }}>
          {triggerLabel || "Change Office"}
        </Typography>
        <ExpandMoreIcon
          style={{
            marginLeft: 2,
            fontSize: 22,
            color: primaryColor,
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: 34,
            right: 0,
            width: 420,
            background: "#f7f7f7",
            border: `1px solid ${Colors.mediumGrey}`,
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            zIndex: 1500,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 10px",
              borderBottom: `1px solid ${Colors.mediumGrey}`,
              background: "#fff",
            }}
          >
            <Typography style={{ color: primaryColor, fontWeight: 700 }}>
              Choose your office
            </Typography>
            <IconButton size="small" onClick={() => setIsOpen(false)}>
              <CloseIcon style={{ fontSize: 16 }} />
            </IconButton>
          </div>

          <div style={{ padding: "8px 10px 10px 10px" }}>
            <Typography
              style={{
                fontSize: 11,
                fontWeight: 600,
                marginBottom: 8,
                color: Colors.black,
              }}
            >
              Select a pharmacy to edit its information or post a shift
            </Typography>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: `1px solid ${Colors.mediumGrey}`,
                borderRadius: 999,
                background: "#fff",
                padding: "6px 12px",
                marginBottom: 10,
              }}
            >
              <SearchIcon
                style={{
                  color: primaryColor,
                  fontSize: 18,
                  marginRight: 8,
                }}
              />
              <InputBase
                fullWidth
                placeholder="Search by owner or location name"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                style={{ fontSize: 13 }}
              />
            </div>

            <div
              ref={listRef}
              onScroll={handleListScroll}
              style={{
                maxHeight: 320,
                overflowY: "auto",
                paddingRight: 2,
              }}
            >
              {rows.map((row: RegionalManagerDashboardRow) => {
                const displayLabel = getRegionalManagerLocationDisplayLabel(row);
                const isSelected =
                  selectedLocationTitle === displayLabel;
                return (
                  <div
                    key={row.locationId}
                    onClick={() => {
                      onSelectLocation({
                        ...row,
                        locationTitleName: displayLabel,
                      });
                      setIsOpen(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      background: "#fff",
                      border: isSelected ? `2px solid ${primaryColor}` : "1px solid #ececec",
                      borderRadius: 8,
                      padding: "10px 10px",
                      marginBottom: 8,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: "#7ed2d7",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12,
                          fontWeight: 700,
                          marginRight: 10,
                        }}
                      >
                        {getInitials(displayLabel)}
                      </div>
                      <Typography
                        style={{ fontSize: 14, color: "#2b3b63", fontWeight: 600 }}
                      >
                        {displayLabel}
                      </Typography>
                    </div>

                    {row.pharmacy?.approval?.status !== "approved" && (
                      <div
                        style={{
                          background: "#ff5f5f",
                          color: "#fff",
                          borderRadius: 999,
                          padding: "5px 10px",
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        Under Review
                      </div>
                    )}
                  </div>
                );
              })}

              {!isLoading && rows.length === 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px 0 16px 0",
                    color: Colors.darkGrey,
                    fontSize: 12,
                  }}
                >
                  No matching locations found.
                </div>
              )}

              {isLoading && (
                <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 12px 0" }}>
                  <CircularProgress size={18} style={{ color: primaryColor }} />
                </div>
              )}
            </div>

            <Typography style={{ fontSize: 11, color: Colors.black }}>
              *Pharmacy which still is under review can be selected, but will not be able to post shifts.
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};
